import {
	formatNominalValue,
	makeSecurityTypeName,
	SECURITY_TYPE_OPTIONS,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import {
	formatNominalValueAsOfDate,
	getCurrentNominalValue,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getCurrentNominalValue';
import {
	isSecurityIssuedInUnits,
	SECURITY_NOMINAL_VALUE_PRECISION,
} from '@dealstack/frontend/shared/cap';
import { createSecurityActionColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/ButtonColumn';
import { createPrismaDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	CURRENCIES_OPTIONS,
	getCurrencyFullNameByCode,
} from '@dealstack/frontend/shared/currencies';
import { NOT_APPLICABLE } from '@dealstack/frontend/shared/other/react/specialSigns';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { formatNumberToUI } from '@dealstack/shared/lib/format/number.ts';
import { GetPersonSecurityDataRes, SecurityCountsDto } from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const getSecuritiesTableColumns: (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	setShowEditSecurityDrawer?: (value: boolean) => void,
	setEditSecurity?: (transaction: GetPersonSecurityDataRes) => void,
	hasEditorPermission?: boolean,
	securityCounts?: SecurityCountsDto,
	filterDate?: Date | null,
	showSplitAdjustedNominalValue?: boolean
) => ExtendedColumnDef<GetPersonSecurityDataRes>[] = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	setShowEditSecurityDrawer?: (value: boolean) => void,
	setEditSecurity?: (transaction: GetPersonSecurityDataRes) => void,
	hasEditorPermission = true,
	securityCounts = undefined,
	filterDate = null,
	showSplitAdjustedNominalValue = false
) => {
	const currentNominalValueLabel = tCapManage('securities.nominal_value_per_unit.as_of', {
		date: formatNominalValueAsOfDate(filterDate),
	});
	const nominalValueLabel = showSplitAdjustedNominalValue
		? tCapManage('securities.nominal_value_per_unit.original')
		: tCapManage('securities.table.columns.nominal_value');
	const baseColumns: ExtendedColumnDef<GetPersonSecurityDataRes>[] = [
		createTextColumn({
			headerText: tCapManage('securities.table.columns.id'),
			filterText: tCapManage('securities.table.filter.id'),
			columnLabel: tCapManage('securities.table.columns.id'),
			accessorKey: 'id',
			enableGlobalFilter: true,
			filterValueType: 'uuid',
			showFilter: true,
			minSize: 360,
			size: 360,
		}),
		createTextColumn({
			headerText: tCapManage('securities.table.columns.name'),
			filterText: tCapManage('securities.table.filter.name'),
			columnLabel: tCapManage('securities.table.columns.name'),
			accessorKey: 'name',
			enableGlobalFilter: true,
			showFilter: true,
			filterVariant: 'text',
		}),
		createTextColumn({
			id: 'type',
			headerText: tCapManage('securities.table.columns.type'),
			filterText: tCapManage('securities.table.filter.type'),
			columnLabel: tCapManage('securities.table.columns.type'),
			accessorFn: (row) => {
				return makeSecurityTypeName(row.type);
			},
			enableGlobalFilter: true,
			filterVariant: 'multi-select',
			showFilter: true,
			showFooter: true,
			text: tCapManage('securities.table.footer.type.count', { defaultValue: 'count' }),
			footerValue: securityCounts?.uniqueTypeCount ?? 0,
			mantineFilterMultiSelectProps: {
				data: SECURITY_TYPE_OPTIONS,
			},
		}),
		createTextColumn({
			id: 'currency',
			headerText: tCapManage('securities.table.columns.currency'),
			filterText: tCapManage('securities.table.filter.currency'),
			columnLabel: tCapManage('securities.table.columns.currency'),
			accessorFn: (security) => getCurrencyFullNameByCode(security.currency),
			enableGlobalFilter: true,
			filterVariant: 'select',
			showFilter: true,
			showFooter: true,
			text: tCapManage('securities.table.footer.currency.count', { defaultValue: 'count' }),
			footerValue: securityCounts?.uniqueCurrency ?? 0,
			mantineFilterSelectProps: {
				data: CURRENCIES_OPTIONS,
			},
		}),
		...(showSplitAdjustedNominalValue
			? [
					createNumberColumn<GetPersonSecurityDataRes>({
						id: 'nominalValuePerUnit',
						headerText: currentNominalValueLabel,
						columnLabel: currentNominalValueLabel,
						accessorFn: (security) =>
							security.type && isSecurityIssuedInUnits(security.issuedIn)
								? formatNominalValue(getCurrentNominalValue(security))
								: NOT_APPLICABLE,
						showFilter: false,
						enableSorting: false,
						lockPrecision: true,
						maxPrecision: SECURITY_NOMINAL_VALUE_PRECISION,
						minPrecision: SECURITY_NOMINAL_VALUE_PRECISION,
					}),
				]
			: []),
		createNumberColumn({
			id: 'nominalValue',
			headerText: nominalValueLabel,
			filterText: tCapManage('securities.table.filter.nominal_value'),
			columnLabel: nominalValueLabel,
			accessorFn: (security) =>
				security.type && isSecurityIssuedInUnits(security.issuedIn)
					? formatNominalValue(security?.nominalValue)
					: NOT_APPLICABLE,
			showFilter: true,
			lockPrecision: true,
			maxPrecision: SECURITY_NOMINAL_VALUE_PRECISION,
			minPrecision: SECURITY_NOMINAL_VALUE_PRECISION,
			showFooter: true,
			text: tCapManage('securities.table.footer.nominalValue.count', {
				defaultValue: 'count',
			}),
			footerValue: securityCounts?.uniqueNominalValue ?? 0,
			footerMinPrecision: 0,
			footerMaxPrecision: 0,
		}),
		createNumberColumn({
			headerText: tCapManage('securities.table.columns.votes_per_unit'),
			filterText: tCapManage('securities.table.filter.votes_per_unit'),
			columnLabel: tCapManage('securities.table.columns.votes_per_unit'),
			id: 'votesPerUnit',
			showFilter: true,
			accessorFn: (security) =>
				security.type && isSecurityIssuedInUnits(security.issuedIn)
					? security?.votesPerUnit
					: NOT_APPLICABLE,
			showFooter: true,
			text: tCapManage('securities.table.footer.votesPerUnit.count', {
				defaultValue: 'count',
			}),
			footerValue: securityCounts?.uniqueVotesPerUnit ?? 0,
		}),
		createNumberColumn({
			headerText: tCapManage('securities.table.columns.authorised_capital', {
				defaultValue: 'Authorised Capital',
			}),
			id: 'authorisedCapital',
			accessorFn: (security) =>
				security?.authorisedCapital
					? formatNumberToUI(security.authorisedCapital)
					: NOT_APPLICABLE,
			showFooter: true,
			text: tCapManage('securities.table.footer.authorisedCapital.count', {
				defaultValue: 'count',
			}),
			footerValue: securityCounts?.uniqueAuthorisedCapital ?? 0,
		}),
		createNumberColumn({
			id: 'totalNominalValue',
			headerText: tCapManage('securities.table.columns.total_nominal_value', {
				defaultValue: 'Total Nominal Value',
			}),
			columnLabel: tCapManage('securities.table.columns.total_nominal_value'),
			accessorFn: (security) => security?.totalNominalValue,
			showFilter: false,
			lockPrecision: true,
			enableSorting: false,
			showFooter: true,
			text: tCapManage('securities.table.footer.total_nominal_value'),
			footerValue: securityCounts?.totalNominalValue ?? 0,
		}),
		createNumberColumn({
			id: 'totalSharePremium',
			headerText: tCapManage('securities.table.columns.total_share_premium', {
				defaultValue: 'Total Share Premium',
			}),
			columnLabel: tCapManage('securities.table.columns.total_share_premium'),
			accessorFn: (security) => security?.totalSharePremium,
			showFilter: false,
			lockPrecision: true,
			enableSorting: false,
			showFooter: true,
			text: tCapManage('securities.table.footer.total_share_premium'),
			footerValue: securityCounts?.totalSharePremium ?? 0,
		}),
		createNumberColumn({
			id: 'totalShareCapital',
			headerText: tCapManage('securities.table.columns.total_share_capital', {
				defaultValue: 'Total Share Capital',
			}),
			columnLabel: tCapManage('securities.table.columns.total_share_capital'),
			accessorFn: (security) => security?.totalShareCapital,
			showFilter: false,
			lockPrecision: true,
			enableSorting: false,
			showFooter: true,
			text: tCapManage('securities.table.footer.total_share_capital'),
			footerValue: securityCounts?.totalShareCapital ?? 0,
		}),
		createNumberColumn({
			id: 'totalVested',
			headerText: tCapManage('securities.table.columns.total_vested', {
				defaultValue: 'Total Vested',
			}),
			columnLabel: tCapManage('securities.table.columns.total_vested'),
			accessorFn: (security) => security?.totalVested,
			showFilter: false,
			lockPrecision: true,
			enableSorting: false,
			showFooter: true,
			text: tCapManage('securities.table.footer.total_vested'),
			footerValue: securityCounts?.totalVested ?? 0,
		}),
		createNumberColumn({
			id: 'totalUnvested',
			headerText: tCapManage('securities.table.columns.total_unvested', {
				defaultValue: 'Total Unvested',
			}),
			columnLabel: tCapManage('securities.table.columns.total_unvested'),
			accessorFn: (security) => security?.totalUnvested,
			showFilter: false,
			lockPrecision: true,
			enableSorting: false,
			showFooter: true,
			text: tCapManage('securities.table.footer.total_unvested'),
			footerValue: securityCounts?.totalUnvested ?? 0,
		}),
		createPrismaDateColumn({
			headerText: tCapManage('securities.table.columns.created'),
			columnLabel: tCapManage('securities.table.columns.created'),
			filterText: tCapManage('securities.table.filter.created'),
			accessorKey: 'createdAt',
			enableColumnFilter: false,
			showFilter: true,
			showFooter: true,
			text: tCapManage('securities.table.footer.createdAt.count', { defaultValue: 'count' }),
			footerValue: securityCounts?.uniqueCreatedAt ?? 0,
		}),
	];

	if (hasEditorPermission) {
		baseColumns.push(
			createSecurityActionColumn<GetPersonSecurityDataRes>(
				setShowEditSecurityDrawer,
				setEditSecurity
			)
		);
	}

	return baseColumns;
};
